import {useEffect,useRef,useState} from 'react';
import {Button} from '@cap4/ui';
import {companyApi} from './api';
import {canConfirmChoice,consentUrl,mergeChoices,roleLabel,safePageUrl} from './model';
import {useExpiry} from './useExpiry';
import type {CompanyConnection,CompanyReconnect,CompanySetup,OrganizationChoices,OwnedCompanyResource} from './types';
import './linkedin-company.css';

export interface LinkedInCompanyConnectProps {
  resources:OwnedCompanyResource[];
  initialResourceId?:string;
  selectionSessionId?:string;
  reconnect?:CompanyReconnect;
  onConnected:(connection:CompanyConnection)=>void|Promise<void>;
  onCancel:()=>void;
}
const errorMessage=(error:unknown)=>error instanceof Error?error.message:'Handlingen kunne ikke gennemføres. Prøv igen.';

export function LinkedInCompanyConnect({resources,initialResourceId,selectionSessionId,reconnect,onConnected,onCancel}:LinkedInCompanyConnectProps) {
  const [resourceId,setResourceId]=useState(reconnect?.resourceId??initialResourceId??'');
  const [setup,setSetup]=useState<CompanySetup|null>(null);
  const [session,setSession]=useState<OrganizationChoices|null>(null);
  const [selected,setSelected]=useState('');
  const [busy,setBusy]=useState(false);
  const [error,setError]=useState('');
  const [completed,setCompleted]=useState<CompanyConnection|null>(null);
  const [reload,setReload]=useState(0);
  const generation=useRef(0);
  const now=useExpiry(session?.expiresAt);
  const owned=resources.some(resource=>resource.id===resourceId);

  useEffect(()=>{
    const current=++generation.current;
    setError('');setSetup(null);setSession(null);setSelected('');setCompleted(null);
    if(!selectionSessionId&&!owned){setBusy(false);return()=>{generation.current++}}
    setBusy(true);
    const request=selectionSessionId?companyApi.choices(selectionSessionId):companyApi.setup(resourceId);
    void request.then(result=>{
      if(current!==generation.current)return;
      if(selectionSessionId){
        const choices=result as OrganizationChoices;
        if(!choices.resourceId||!Number.isFinite(Date.parse(choices.expiresAt))||!Array.isArray(choices.organizations))throw new Error('Virksomhedsvalget kunne ikke hentes. Start forbindelsen igen.');
        setSession(choices);
      }else setSetup(result as CompanySetup);
    }).catch(error=>{if(current===generation.current)setError(errorMessage(error))})
      .finally(()=>{if(current===generation.current)setBusy(false)});
    return()=>{generation.current++};
  },[selectionSessionId,resourceId,owned,reload]);

  const run=async(action:()=>Promise<void>)=>{
    if(busy)return;
    const current=generation.current;setBusy(true);setError('');
    try{await action()}catch(error){if(current===generation.current)setError(errorMessage(error))}
    finally{if(current===generation.current)setBusy(false)}
  };
  const start=()=>run(async()=>{
    if(!owned||!setup?.canConnect)return;
    const current=generation.current;
    const result=await companyApi.start(resourceId,reconnect?.connectionId);
    if(current===generation.current)window.location.assign(consentUrl(result.url));
  });
  const loadMore=()=>run(async()=>{
    if(!selectionSessionId||!session?.nextCursor)return;
    const current=generation.current;
    const page=await companyApi.choices(selectionSessionId,session.nextCursor);
    if(current!==generation.current)return;
    if(page.resourceId!==session.resourceId||page.expiresAt!==session.expiresAt||page.expectedOrganizationUrn!==session.expectedOrganizationUrn)throw new Error('Virksomhedsvalget er ændret. Hent listen igen.');
    setSession({...page,organizations:mergeChoices(session.organizations,page.organizations),discoveryWarning:page.discoveryWarning??session.discoveryWarning});
  });
  const confirm=()=>run(async()=>{
    if(!selectionSessionId||!canConfirmChoice(session,selected,resources,Date.now()))return;
    const current=generation.current;
    const result=await companyApi.complete(selectionSessionId,selected);
    if(current!==generation.current)return;
    setCompleted(result); // A failed parent refresh must never repeat session consumption.
    await onConnected(result);
  });
  const cancel=()=>run(async()=>{
    const current=generation.current;
    if(selectionSessionId&&!completed)await companyApi.cancel(selectionSessionId);
    if(current===generation.current)onCancel();
  });
  const localResource=resources.find(resource=>resource.id===session?.resourceId);
  const expired=Boolean(session&&Date.parse(session.expiresAt)<=now);

  return <section className="li-company" aria-label="Forbind LinkedIn-virksomhed" aria-busy={busy}>
    <p>Forbind en LinkedIn-side, som du administrerer, til en virksomhedskanal i CAP4Publish.</p>
    {error&&<p className="li-company-error" role="alert">{error}</p>}
    {completed?<>
      <p role="status">Virksomheden er forbundet.</p>
      <Button onClick={()=>run(()=>Promise.resolve(onConnected(completed)))} disabled={busy}>Opdatér forbindelser</Button>
    </>:selectionSessionId?<>
      {session&&<>
        <p><strong>Kanal i CAP4Publish:</strong> {localResource?.name??'Kanalen er ikke længere tilgængelig for dig'}</p>
        {session.expectedOrganizationUrn&&<p>Genforbindelse gælder den allerede tilknyttede LinkedIn-side.</p>}
        {session.discoveryWarning&&<p role="status">{session.discoveryWarning.reason}</p>}
        {expired?<p role="status">Virksomhedsvalget er udløbet. Luk og start forbindelsen igen.</p>:<p>Vælg LinkedIn-siden nedenfor. Vi kontrollerer din administratorrolle igen, når du bekræfter.</p>}
        <fieldset disabled={busy||expired||!localResource}>
          <legend>Virksomhed på LinkedIn</legend>
          {session.organizations.map(choice=><div className="li-company-choice" key={choice.organizationUrn}>
            <label><input type="radio" name="linkedin-company" value={choice.organizationUrn} checked={selected===choice.organizationUrn} disabled={Boolean(session.expectedOrganizationUrn&&choice.organizationUrn!==session.expectedOrganizationUrn)} onChange={()=>setSelected(choice.organizationUrn)}/>
              <span><strong>{choice.displayName}</strong><small>{roleLabel(choice.role)}</small></span></label>
            {safePageUrl(choice.pageUrl)&&<a href={safePageUrl(choice.pageUrl)} target="_blank" rel="noopener noreferrer">Se LinkedIn-side</a>}
          </div>)}
          {!session.organizations.length&&<p>Ingen godkendt virksomhed fundet på denne side af listen. {session.nextCursor?'Hent flere virksomheder.':'Kontrollér din administratorrolle og appens adgang på LinkedIn.'}</p>}
        </fieldset>
        {session.nextCursor&&<Button variant="secondary" disabled={busy||expired} onClick={loadMore}>Hent flere virksomheder</Button>}
        <Button disabled={busy||!canConfirmChoice(session,selected,resources,now)} onClick={confirm}>Bekræft virksomhed</Button>
      </>}
      {!session&&!busy&&<Button variant="secondary" onClick={()=>setReload(value=>value+1)}>Hent virksomhedsvalg igen</Button>}
    </>:<>
      <label className="li-company-resource">Kanal i CAP4Publish
        <select value={resourceId} disabled={busy||Boolean(reconnect)} onChange={event=>setResourceId(event.target.value)}>
          <option value="">Vælg virksomhedskanal</option>
          {resources.map(resource=><option key={resource.id} value={resource.id}>{resource.name}</option>)}
        </select>
      </label>
      {!resources.length&&<p>Du skal eje en virksomhedskanal for at forbinde LinkedIn.</p>}
      {reconnect&&<p>Genforbinder {reconnect.displayName}. Den eksisterende forbindelse bevares, hvis du afbryder.</p>}
      {setup&&<p role="status">{setup.reason}</p>}
      {error&&owned&&!setup&&<Button variant="secondary" disabled={busy} onClick={()=>setReload(value=>value+1)}>Kontrollér adgang igen</Button>}
      <Button disabled={busy||!owned||!setup?.canConnect} onClick={start}>Fortsæt til LinkedIn</Button>
    </>}
    {busy&&<p role="status">Kontrollerer forbindelsen …</p>}
    <div className="li-company-actions"><Button variant="ghost" disabled={busy} onClick={cancel}>{completed?'Luk':'Annullér'}</Button></div>
  </section>;
}
